import {useCallback, useEffect, useMemo} from 'react';

import {useRunWorkspaceRunContextMaybe} from '../../pages/Run/RunWorkspaceRunContext';
import {trackWorkspaceBanners} from '../../services/analytics/workspaceBannersEvents';
import {usePropsSelector, useSelector} from '../../state/hooks';
import {useViewer} from '../../state/viewer/hooks';
import {Viewer} from '../../state/viewer/types';
import * as ViewActions from '../../state/views/actions';
import {usePart, useViewAction} from '../../state/views/hooks';
import {makeNumPanelsSelector} from '../../state/views/panelBankConfig/selectors';
import {RunPageNormalized} from '../../state/views/runPage/types';
import {LoadableView} from '../../state/views/types';
import {isOwnView} from '../../state/views/util';
import {getNamedWorkspaceIdForView} from '../../state/workspaces/names';
import {
  useNwPermissions,
  viewerCanWriteToNw,
} from '../../state/workspaces/nwPermissions';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsContext} from '../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {
  OLD_CLI_WARNING_CUTOFF,
  PANEL_GEN_INFO_NUM_PANELS_THRESHOLD,
} from './constants';
import {WorkspaceBannerType} from './types';

export function useIsSaveWorkspaceAllowed(
  view: LoadableView,
  viewer: Viewer | undefined
): boolean {
  const isOwnWorkspace = isOwnView(viewer, view);
  const nwPermissions = useNwPermissions();
  const nwId = getNamedWorkspaceIdForView(view.name, view.user);
  const isOwnPersonalWorkspace =
    isOwnWorkspace && nwId.nwType === 'personal workspace';
  const isWritableSharedView =
    nwId.nwType === 'shared workspace' &&
    viewerCanWriteToNw(nwPermissions.data);
  return isOwnPersonalWorkspace || isWritableSharedView;
}

export const useWorkspaceBanners = () => {
  const {viewRef} = useWorkspaceRefsContext();
  const viewPart = usePart(viewRef);
  const viewer = useViewer();
  const view = useSelector(state => state.views.views[viewRef.viewID]);

  const hideWorkspaceBanner = useViewAction(
    viewRef,
    ViewActions.hideWorkspaceBanner
  );
  const dismissWorkspaceBanner = useCallback(
    (bannerType: WorkspaceBannerType) => {
      hideWorkspaceBanner(bannerType);
      trackWorkspaceBanners({
        bannerType,
        event: 'Banner dismissed',
      });
    },
    [hideWorkspaceBanner]
  );

  /* --- CLI version warning --- */
  const run = useRunWorkspaceRunContextMaybe();
  const shouldShowCliVersionWarning = useMemo(() => {
    if (!run || viewRef.type !== 'run-view') {
      return false;
    }
    const cliVersion = run?._wandb['value.cli_version'];
    const createdAt = new Date(run?.createdAt).valueOf();
    const isUsingOldCliVersion =
      createdAt >= OLD_CLI_WARNING_CUTOFF &&
      typeof cliVersion === 'string' &&
      /^0\.[89](\..+)?$/.test(cliVersion);
    return (
      isUsingOldCliVersion &&
      (viewPart as RunPageNormalized).cliVersionWarningHiddenAt == null
    );
  }, [run, viewPart, viewRef.type]);

  useEffect(() => {
    if (shouldShowCliVersionWarning) {
      trackWorkspaceBanners({
        bannerType: 'cli-version-warning',
        event: 'Banner displayed',
      });
    }
  }, [shouldShowCliVersionWarning]);

  /* --- Panel generation banners --- */
  const {panelAutoGenInfoHiddenAt} = viewPart;
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {
    workspaceLayout: {shouldAutoGeneratePanels},
  } = useWorkspaceSettingsContext();

  const canSaveWorkspace = useIsSaveWorkspaceAllowed(view, viewer);

  // computing numPanels can be expensive in large workspaces, so
  // do the cheap checks first and only get numPanels if necessary
  const shouldCheckNumPanels = useMemo(
    () => canSaveWorkspace && !panelAutoGenInfoHiddenAt,
    [canSaveWorkspace, panelAutoGenInfoHiddenAt]
  );
  const numPanels = usePropsSelector(
    makeNumPanelsSelector,
    panelBankConfigRef,
    !shouldCheckNumPanels
  );

  // preventative banner to raise awareness of panel gen setting
  const shouldShowPanelAutoGenInfo = useMemo(
    () =>
      shouldAutoGeneratePanels &&
      !panelAutoGenInfoHiddenAt &&
      numPanels != null &&
      numPanels >= PANEL_GEN_INFO_NUM_PANELS_THRESHOLD,
    [shouldAutoGeneratePanels, panelAutoGenInfoHiddenAt, numPanels]
  );

  useEffect(() => {
    if (shouldShowPanelAutoGenInfo) {
      trackWorkspaceBanners({
        bannerType: 'panel-auto-gen-info',
        event: 'Banner displayed',
      });
    }
  }, [shouldShowPanelAutoGenInfo]);

  /* --- Return value --- */
  return useMemo(() => {
    return {
      dismissWorkspaceBanner,
      shouldShowCliVersionWarning,
      shouldShowPanelAutoGenInfo,
    };
  }, [
    dismissWorkspaceBanner,
    shouldShowCliVersionWarning,
    shouldShowPanelAutoGenInfo,
  ]);
};
